import { PolicyStatus } from '@/enums/policy-status.enum';
import { TermPaymentDetails } from '@/interfaces/entities/term-payment-details.interface';

export interface PolicyHolder extends Person {
  partyID: string;
}

export interface Person {
  name: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
}
export interface Coverage {
  coverageType: InsurancePolicyCoverageTypes;
  coverageCode: string;
  isPolicyChangeAllowed: boolean;
  id: string;
  isMotor: boolean;
  isHome: boolean;
  isPet: boolean;
  isBoat: boolean;
  canClaimOnline: boolean;
  isHealth: boolean;
  discounts: string[];
  digitalChannelChangeAllowed: boolean;
  riskEffectiveDate: Date | null;
  coveragePatternCode: string;
  discriminator: string;
}

export enum InsurancePolicyCoverageTypes {
  MotorComprehensive = 0,
  MotorComprehensiveOther = 1,
  Home = 2,
  Contents = 3,
  HomeAndContents = 4,
  MotorThirdPartyLiability = 5,
  MotorFireTheftAndThirdPartyLiability = 6,
  MotorCtp = 7,
  MotorClassic = 8,
  MotorUnique = 9,
  Motorcycle = 10,
  Caravan = 11,
  BoatComprehensive = 12,
  BoatThirdPartyLiability = 13,
  Pet = 14,
  MotorThirdPartyLiabilityOther = 15,
  MotorFireTheftAndThirdPartyLiabilityOther = 16,
  RACQAccidentOnly = 17,
  RACQBasicHospital = 18,
  RACQMidHospital = 19,
  RACQComprehensiveHospital = 20,
  RACQTopHospital = 21,
  RACQAccidentOnlyMinimumExtras = 22,
  RACQBasicHospitalMinimumExtras = 23,
  RACQBasicHospitalBasicExtras = 24,
  RACQMidHospitalMinimumExtras = 25,
  RACQMidHospitalMidExtras = 26,
  RACQComprehensiveHospitalMidExtras = 27,
  RACQComprehensiveHospitalComprehensiveExtras = 28,
  RACQTopHospitalMidExtras = 29,
  RACQTopHospitalTopExtras = 30,
}

export interface Driver extends Person {
  isPrimary: boolean;
  excess: number | null;
}

export interface Vehicle1 {
  nvic: string;
  year: string;
  make: string;
  model: string;
  registrationNumber: string;
  colour: string;
  doors: string;
  series: string;
  transmission: string;
  accessories: string[];
  modifications: string[];
  otherModifications: VehicleAccessory[];
  formattedSummary: string;
  formattedLine1: string;
  formattedLine2: string;
}

export interface VehicleAccessory {
  code: string;
  externalId: string;
}

export interface VehicleCoverage extends Coverage {
  vehicle: Vehicle1;
  benefits: string[];
  drivers: Driver[];
  basicExcessCode: string;
  highValueBasicExcess: number | null;
  highPerformanceExcess: number | null;
  hasListedDriverUnder25: boolean;
  sumInsured: number | null;
  agreedValue: number | null;
  isMarketValue: boolean;
  marketValue: number | null;
  garagingType: string;
  garagingLocation: string;
  isVehicleUsePrivate: boolean;
  finance: string;
  typeOfFinanceCode: string;
  hasAdHocCondition: boolean;
  hasDriverExcessCondition: boolean;
  pdsVersion: string;
  spdsVersion: string;
  riskEffectiveDate: Date | null;
  garageState?: string;
}

export interface PetCoverage extends Coverage {
  petType: string;
  petBreed: string;
  petName: string;
  dateOfBirth: string;
  petLocationDescription: string;
  basicExcessCode: string;
  hasTlcCover: boolean;
  pdsVersion: string;
  spdsVersion: string;
  riskEffectiveDate: Date | null;
}

export interface IAddress {
  dpid: number | null;
  postalDeliveryType: string;
  postalDeliveryNumber: string;
  salutation: string;
  unit: string;
  streetNumber: string;
  street: string;
  suburb: string;
  postcode: string;
  state: string;
  country: string;
  formattedStreet: string;
  formattedLong: string;
  experianValidationStatus: string;
  occupancyTypeCode: string;
  unitType: string;
  buildingLevel: string;
  returnMailFlag: boolean | null;
}

export interface ResidentialCoverage extends Coverage {
  riskAddress: IAddress;
  ownerOccupancyCode: string;
  mortgagee: string[];
  buildingSumInsured: number | null;
  generalContentsAmount: number | null;
  totalContentsAmount: number | null;
  landlordFixturesAndFittings: number | null;
  propertyType: string;
  propertyElevated: boolean | null;
  numOfStoreys: string;
  occupiedFloor: string;
  windowProtection: string;
  buildingExcessCode?: string;
  contentsExcessCode?: string;
  mobilePhones?: MobilePhone[] | null;
  portableItems?: ContentsItem[] | null;
  homeItems?: ContentsItem[] | null;
  hasBuildingAdvancedCover: boolean;
  hasContentsAdvancedCover: boolean;
  hasBuildingMotorBurnout: boolean;
  hasContentsMotorBurnout: boolean;
  pets?: PetItem[] | null;
  petsCovered?: number | null; // number of pets covered
  hasSmallBusinessContentsCover: boolean;
  pdsVersion?: string | null;
  spdsVersion?: string | null;
  groupCoverCode?: string | null;
}

export interface PetItem {
  petName: string;
  petTypeCode: string;
  fixedId: string;
}

export interface ContentsItem {
  itemTypeCode: string;
  itemDescription: string;
  value: number;
  fixedId: string;
}
export interface MobilePhone {
  value: number; // item value
  description: string;
  fixId: string;
}
export interface FutureChange {
  effectiveDate: Date | null;
  documentId: string;
}
export interface InsurancePolicy {
  policyNumber: string;
  jobType: string;
  producerCode: string;
  isBrokerPolicy: boolean;
  mrmExternalID: string;
  status: PolicyStatus;
  premium: number | null;
  termStart: Date | null;
  termEnd: Date | null;
  termType: string;
  renewalTermStart?: Date;
  renewalTermEnd?: Date;
  orderByDateTime: string | null;
  cancellationDate?: Date | null;
  currentDocumentId: string;
  renewalDocumentId: string;
  certOfCurrencyDocumentId: string;
  isCertOfCurrencyGenAllowed: boolean;
  state: {
    isPolicyCancelAllowed: boolean;
    isPolicyChangeAllowed: boolean;
    canDeferPayment: boolean;
    canChangePaymentDetails: boolean;
    canSwitchToMonthlyPayments: boolean;
    canReducePremium: boolean;
    isInRenewal: boolean;
    isPaymentDue: boolean;
    hasMultiplePaymentsDue: boolean;
    hasDraftRenewal: boolean;
    isOverdue: boolean;
    isFutureCancelled: boolean;
    hasSingleFutureChange: boolean;
    hasMultipleInSequenceFutureChanges: boolean;
    hasMultipleOutOfSequenceFutureChanges: boolean;
    hasCostOverride: boolean;
    policyChangeIssues: {
      hasOpenWorkOrderFromOtherUser: boolean;
      renewalTermHasFutureChange: boolean;
    };
  };
  currentTermPaymentDetails: TermPaymentDetails;
  renewalTermPaymentDetails: TermPaymentDetails;
  coverages: Coverage[];
  policyHolders: PolicyHolder[];
  futureChanges: FutureChange[];
  futureRenewalPreference: boolean | null;
  currentRenewalPreference: boolean | null;
  renewalTermAcceptance: boolean | null;
  rejectionReason: string;
}

export interface VehicleBody2 {
  code: string;
  description: string;
  assistanceEligibility?: string;
  colour?: string;
  colourCode?: string;
  doors?: number | null;
}

export interface VehicleOption2 {
  doors: string;
  series: string;
  transmission: string;
}

export interface VehicleFuelType {
  code: string;
}

export interface VehicleEligibilityCheckResponse {
  productName: string;
  level: string;
  reason: string;
  isEligible: boolean;
}

export interface InsuranceVehicleDetail {
  id?: string;
  nvic: string;
  glassesId: number;
  registrationNumber: string;
  stateCode: string;
  make: VehicleMake2;
  model: VehicleModel2;
  variant: VehicleVariant2;
  body?: VehicleBody2;
  option: VehicleOption2;
  manufacturingYear: number | null;
  acceptance?: string | null;
  marketValue: number;
  retailValue?: number;
  tradeValue?: number;
  tradeLowValue?: number;
  points: number | null;
  hasTurbo: boolean;
  gears: number;
  driveType: string;
  cylinders: string;
  engineCapacity: number;
  fuelType: VehicleFuelType;
  source: string;
  weight?: number | null;
  weightUnit?: string;
  kerbWeight?: number | null;
  kerbWeightUnit?: string;
  length?: number | null;
  lengthUnit?: string;
  loadCapacity?: number | null;
  loadCapacityUnit?: string;
  totalWeight?: number;
  vehicleType: string;
  eligibility?: VehicleEligibilityCheckResponse;
  highPerformance?: string;
}

export interface VehicleMake2 {
  code: string;
  description: string;
  assistanceEligibility?: string;
}

export interface VehicleModel2 {
  code: string;
  description: string;
  assistanceEligibility?: string;
}

export interface VehicleVariant2 {
  code: string;
  description: string;
  assistanceEligibility?: string;
}

export interface Driver2 {
  driverId: string;
  externalId: string;
  name: string;
  isPrimary: boolean;
  isUnder25: boolean;
  excess: number | null;
  longTermClaims: number;
  shortTermClaims: number;
  yearsLicenced: number;
  isRatingOne: boolean | null;
  yearsRatingOneHeld: number | null;
  isAllowedToChangeYearsRatingOneHeld: boolean | null;
  dateOfBirth: Date;
  licenceLossCount: number;
  insuranceDeclinesCount: number;
  criminalConvictionsCount: number;
}

export interface InsuranceSummaryModel {
  /** Policy number */
  policyNumber: string;
  orderByDateTime?: Date;
  termEnd?: Date;
  /** Policy cancellation date */
  cancellationDate?: Date;
  /** Flags indicating the state of the policy */
  state: any; //State;
  inRenewalNonResponsive: boolean;
  inRenewalResponsiveWithPaymentDue: boolean;
  responsiveWithPaymentDue: boolean;
  paymentReferenceNumber: string;
  coverageCode: string;
  coverageSummary: string;
  isBrokerPolicy: boolean;
  nextInvoiceAmount?: number;
  isHealthInsurance: boolean;
  isHomeInsurance: boolean;
  shouldShowInfoBanner: boolean;
  shouldShowCycloneUpdateButton: boolean;
  showChangeAddress: boolean;
  coverageId: string;
  fullAddress: string;
  renewalTermStartDate?: Date;
  currentRenewalPreference?: boolean;
  futureRenewalPreference?: boolean;
  status: PolicyStatus;
}

interface PaymentDetails {
  termNumber: 0;
  isPaymentDue: false;
  hasMultiplePaymentsDue: false;
  totalAmountDue: null;
  nextInvoiceAmountDue: null;
  nextInvoiceDueDate: null;
  nextInvoiceOriginalDueDate: null;
  currentInvoiceDueDate: null;
  followingInvoiceDueDate: null;
  overdueAmount: null;
  overdueInceptionDate: null;
  includesInstalmentFees: false;
  hasPartiallyPaidInvoices: false;
}

export interface ResponsivePaymentDetails extends PaymentDetails {
  paymentReferenceNumber: string;
}
